import { put, call, all, takeLatest, takeEvery } from 'redux-saga/effects';

import AuthUtil from '../../AuthUtil';
import { ACCESS_REQUESTS } from '../../../components/global/Server/endpoints';
import { showErrorMessage } from '../../actions/global';
import { storeResponseMessage } from '../../actions/settings';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import {
  FETCH_ACCESS_REQUESTS,
  FETCH_PENDING_ACCESS_FOR_USERS,
  DECIDE_ACCESS_REQUEST,
} from '../../constants/accessRequests';
import {
  fetchAccessRequests,
  storeIncomingAccessRequests,
  storeOutgoingAccessRequests,
  storePendingAccessForUsers,
} from '../../actions/accessRequests';

const DECISION_URL = {
  approve: ACCESS_REQUESTS.APPROVE,
  reject: ACCESS_REQUESTS.REJECT,
  cancel: ACCESS_REQUESTS.CANCEL,
  revoke: ACCESS_REQUESTS.REVOKE,
};

/**
 * Both queues in one pass. They are fetched together because the drawer
 * shows them together, and because an owner of two locations is usually
 * on both sides of the same relationship.
 *
 * Failures are swallowed rather than surfaced: this runs on the alert
 * poll, and a user who is not owner-tier gets an empty result by design —
 * an error toast every 30 seconds would be noise, not information.
 */
function* fetchAccessRequestsSaga(action) {
  const locationId = action.data?.locationId;
  const companyId = action.data?.companyId;
  if (!locationId && !companyId) return;
  try {
    const [incoming, outgoing] = yield all([
      call(() =>
        AuthUtil.postRequest(ACCESS_REQUESTS.LIST_INCOMING, { locationId })
      ),
      call(() =>
        AuthUtil.postRequest(ACCESS_REQUESTS.LIST_OUTGOING, { companyId })
      ),
    ]);
    if (incoming?.status === 200 && incoming.data?.success) {
      yield put(
        storeIncomingAccessRequests(
          incoming.data.pending,
          incoming.data.decided
        )
      );
    }
    if (outgoing?.status === 200 && outgoing.data?.success) {
      yield put(
        storeOutgoingAccessRequests(
          outgoing.data.pending,
          outgoing.data.decided
        )
      );
    }
  } catch (error) {
    // Silent by design — see the note above.
  }
}

function* fetchPendingAccessForUsersSaga(action) {
  const userIds = (action.userIds || []).filter(Boolean);
  if (!userIds.length) {
    yield put(storePendingAccessForUsers([]));
    return;
  }
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(ACCESS_REQUESTS.PENDING_FOR_USERS, { userIds })
    );
    if (response.status === 200 && response.data?.success) {
      yield put(storePendingAccessForUsers(response.data.requests));
    }
  } catch (error) {
    // Same reasoning: the chips are an enhancement, not the source of truth.
  }
}

function* decideAccessRequestSaga(action) {
  const url = DECISION_URL[action.decision];
  if (!url) return;
  try {
    const response = yield call(() => AuthUtil.postRequest(url, action.data));
    const ok = response.status === 200 && response.data?.success;
    if (ok) {
      yield put(storeResponseMessage(response.data.message || 'Done.'));
      // Re-read both queues rather than patching state locally: approving
      // moves a row between two lists AND changes the employee's access,
      // and a stale queue here is a permissions display that lies.
      yield put(
        fetchAccessRequests({
          locationId: action.data?.locationId,
          companyId: action.data?.companyId,
        })
      );
    } else {
      yield put(
        showErrorMessage(response.data?.message || SERVER_FAILED_ERROR_MESSAGE)
      );
    }
    if (action.callback) action.callback(ok, response.data);
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
    if (action.callback) action.callback(false, null);
  }
}

export default function* accessRequestsSaga() {
  yield takeLatest(FETCH_ACCESS_REQUESTS, fetchAccessRequestsSaga);
  yield takeLatest(
    FETCH_PENDING_ACCESS_FOR_USERS,
    fetchPendingAccessForUsersSaga
  );
  // takeEvery, not takeLatest: deciding two requests in quick succession
  // must run both, not drop the first.
  yield takeEvery(DECIDE_ACCESS_REQUEST, decideAccessRequestSaga);
}
